import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '@app/auth/local-storage.service';
import { PxdColumns } from '@app/shared/kendoui/grid/interfaces/pxd-columns';
import { ColumnComponent, GridComponent } from '@progress/kendo-angular-grid';
import { Align } from '@progress/kendo-angular-popup';

@Injectable()
export class PxdGridMenuService {
  private localStorage = inject(LocalStorageService);

  private userSettings: string = 'UserSettings';
  private columnSettingsKey: string;
  private originalColumns: PxdColumns[];
  public columns: PxdColumns[];
  public show: boolean = false;
  public grid: GridComponent;

  //Notes...
  // 1) In order for locking/freezing to work, all columns must have a 'width' set
  //    - we may want to add an 'enableFreezing' option since it won't always make sense to enable
  // 2) In order for exporting to work, a 'kendo-grid-excel' component must be added/configured
  //    - again, it may make sense to add an 'enableExport' option.
  // 3) Right now, the grid-menu expects columns to be of the custom 'Column' type.
  public popupAlign: Align = { horizontal: 'right', vertical: 'top' };
  public anchorAlign: Align = { horizontal: 'right', vertical: 'bottom' };

  get isColumnSettingsEnabled(): boolean {
    return this.columnSettingsKey != null && this.columnSettingsKey !== "";
  }

  public init(columns: PxdColumns[], grid: GridComponent, columnSettingsKey?: string) {
    this.columns = columns;
    this.originalColumns = this.copyColumns(columns);
    this.columnSettingsKey = columnSettingsKey;

    this.grid = grid;

    setTimeout(() => {
      this.setColumnsFromLocalStorage();
    })
  }

  public onToggle(): void {
    this.show = !this.show;
  }

  public restoreColumns(): void {
    this.columns.forEach((c) => {
      let original = this.originalColumns.find(f => f.field === c.field);

      c.params.hide = original.params && original.params.hide;
      c.params.lock = original.params && original.params.lock;
    });

    setTimeout(() => {
      this.grid.columns.forEach((r, i) => {
        let col = this.originalColumns.findIndex(f => f.field === (r as ColumnComponent).field && !r.hidden);

        if (col > -1) {
          this.grid.reorderColumn(r, col);
        }
      });

      this.saveLocalStorage();

      this.show = false;
    }, 1000);
  }

  public lockChanged(event, index) {
    let unlocked = this.columns.filter(f => (f.params.lock === false || !f.params.lock) && f.params.type !== 'row-select' && f.params.display === true);

    if (unlocked.length > 1) {
      this.columns[index].params.lock = event.target.checked;
    } else {
      alert('You must have at least one unhidden and unlocked field.');
      event.target.checked = false;
    }

    this.saveLocalStorage();
  }

  public hideChanged(event, index) {
    if (event.target.checked === false) {
      const unhidden = this.columns.filter((f) => {
        return (f.params.hide === false || !f.params.hide) && f.params.lock === false && f.params.display === true;
      });

      if (unhidden.length > 1) {
        this.columns[index].params.hide = event.target.checked === true ? false : true;
      } else {
        alert('You must have at least one unhidden and unlocked field.');
        event.target.checked = true;
      }
    } else {
      this.columns[index].params.hide = false;
    }

    this.saveLocalStorage();
  }

  public exportToExcel(grid: GridComponent): void {
    grid.saveAsExcel();
  }

  private saveLocalStorage() {
    if (!this.isColumnSettingsEnabled) {
      return;
    }

    const settings = localStorage.getItem(this.userSettings);

    let data: any = {};
    data[this.columnSettingsKey] = null;

    if (settings) {
      data = JSON.parse(settings);
    }

    data[this.columnSettingsKey] = this.columns;

    this.localStorage.setItem(this.userSettings, data);
  }

  private setColumnsFromLocalStorage() {
    if (!this.isColumnSettingsEnabled) {
      return;
    }

    const storedData = JSON.parse(localStorage.getItem(this.userSettings));

    if (storedData && storedData[this.columnSettingsKey]) {
      let storedCols: any[] = (storedData[this.columnSettingsKey] as any[]).sort((obj1, obj2) => obj1.params.order - obj2.params.order);

      this.columns.forEach((col) => {
        let storedCol = storedCols.find(f => f.field === col.field);

        if (storedCol != null && storedCol.params != null) {
          col.params.hide = storedCol.params.hide;
          col.params.lock = storedCol.params.lock;
        }
      });
    }
  }

  private copyColumns(source: PxdColumns[]): PxdColumns[] {
    return source.map((m) => {
      let main = Object.assign({}, m);
      let params = Object.assign({}, m.params);
      main.params = params;

      return main;
    });
  }
}